import React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import StbMarkdown from '../../../security-toolbox-ui/StbMarkdown';
import { ChatMessage, McpToolTrace } from '../../../../types/aiTypes';

import ChatArtifactsRenderer from './ChatArtifactsRenderer';

type Props = {
  messages: ChatMessage[];
  toolTraces?: McpToolTrace[];
  loading?: boolean;
};

const SUPPORTED_ARTIFACT_TYPES = new Set([
  'metric_cards',
  'bar_chart',
  'line_chart',
  'area_chart',
  'pie_chart',
  'table',
  'callout',
  'json',
  'timeline',
  'flow_graph',
]);

function safeJsonParse(value: any): any {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'object') return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  return null;
}

function asArray(value: any): any[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
}

function isArtifact(value: any) {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    SUPPORTED_ARTIFACT_TYPES.has(String(value.type || '')),
  );
}

function normalizeArtifactContainer(value: any): any[] {
  const parsed = safeJsonParse(value) ?? value;

  if (!parsed) return [];

  if (Array.isArray(parsed)) {
    return parsed.filter(isArtifact);
  }

  if (isArtifact(parsed)) {
    return [parsed];
  }

  if (typeof parsed === 'object') {
    const nested =
      parsed.ui_artifacts ||
      parsed.artifacts ||
      parsed.visualizations ||
      parsed.charts ||
      parsed.widgets ||
      parsed.rich_content ||
      parsed.richContent ||
      null;

    if (nested) {
      return normalizeArtifactContainer(nested);
    }
  }

  return [];
}

function dedupeArtifacts(artifacts: any[]) {
  const seen = new Set<string>();
  const out: any[] = [];

  artifacts.forEach((artifact) => {
    if (!isArtifact(artifact)) return;

    let key = '';

    try {
      key = JSON.stringify(artifact);
    } catch {
      key = `${artifact.type}-${artifact.title || ''}-${out.length}`;
    }

    if (seen.has(key)) return;

    seen.add(key);
    out.push(artifact);
  });

  return out;
}

function collectMetadataArtifacts(metadata: any): any[] {
  if (!metadata || typeof metadata !== 'object') return [];

  const candidates = [
    metadata.ui_artifacts,
    metadata.artifacts,
    metadata.visualizations,
    metadata.charts,
    metadata.widgets,
    metadata.rich_content,
    metadata.richContent,

    metadata.mcp_payload?.ui_artifacts,
    metadata.mcp_payload?.artifacts,
    metadata.mcp_payload?.visualizations,
    metadata.mcp_payload?.charts,
    metadata.mcp_payload?.widgets,

    metadata.mcp_result?.ui_artifacts,
    metadata.mcp_result?.artifacts,
    metadata.mcp_result?.visualizations,
    metadata.mcp_result?.charts,
    metadata.mcp_result?.widgets,

    metadata.mcp_result?.result?.ui_artifacts,
    metadata.mcp_result?.result?.artifacts,
    metadata.mcp_result?.result?.visualizations,
    metadata.mcp_result?.result?.charts,
    metadata.mcp_result?.result?.widgets,

    metadata.rag?.ui_artifacts,
    metadata.rag?.artifacts,
    metadata.rag?.visualizations,
    metadata.rag?.charts,
    metadata.rag?.widgets,
  ];

  return candidates.flatMap((candidate) => normalizeArtifactContainer(candidate));
}

function artifactFromFence(language: string, raw: string): any[] {
  const lang = String(language || '').trim().toLowerCase();
  const parsed = safeJsonParse(raw);

  if (!parsed) return [];

  if (['artifact', 'artifacts', 'ui_artifact', 'ui_artifacts', 'visualization', 'visualizations', 'chart', 'charts', 'graph'].includes(lang)) {
    return normalizeArtifactContainer(parsed);
  }

  if (lang === 'json') {
    return normalizeArtifactContainer(parsed);
  }

  return [];
}

function stripRichArtifactFences(content: string): {
  markdown: string;
  artifacts: any[];
} {
  const source = String(content || '');
  const artifacts: any[] = [];

  if (!source) {
    return {
      markdown: '',
      artifacts: [],
    };
  }

  const fenceRegex = /```([A-Za-z0-9_-]*)\s*\n([\s\S]*?)```/g;

  const markdown = source.replace(fenceRegex, (fullMatch, language, body) => {
    const foundArtifacts = artifactFromFence(language, body);

    if (!foundArtifacts.length) {
      return fullMatch;
    }

    artifacts.push(...foundArtifacts);

    // Remove the artifact JSON block from markdown rendering because it is
    // rendered as an interactive/rich component below.
    return '';
  });

  return {
    markdown: markdown.replace(/\n{3,}/g, '\n\n').trim(),
    artifacts,
  };
}

type NormalizedCitation = { label: string; text?: string; score?: number | null };

// Citations come through as `metadata.rag.chunks` for both the standard RAG
// path and the agentic knowledge-base tool; `metadata.drift.citations` is a
// legacy alias kept for backward compatibility. Prefer whichever is present.
function collectCitations(metadata: any): NormalizedCitation[] {
  if (!metadata || typeof metadata !== 'object') return [];

  const driftCitations = Array.isArray(metadata.drift?.citations)
    ? metadata.drift.citations
    : [];

  if (driftCitations.length) {
    return driftCitations.map((c: any, i: number) => ({
      label: c?.title || c?.filename || c?.source || `Citation ${i + 1}`,
      text: c?.text,
      score: c?.score,
    }));
  }

  const ragChunks = Array.isArray(metadata.rag?.chunks) ? metadata.rag.chunks : [];

  return ragChunks.map((c: any, i: number) => ({
    label: c?.source || c?.title || c?.filename || `Citation ${i + 1}`,
    text: c?.text,
    score: c?.score,
  }));
}

function buildMessageRenderPayload(message: ChatMessage) {
  const content = String(message.content || '');
  const stripped = stripRichArtifactFences(content);
  const metadataArtifacts = collectMetadataArtifacts(message.metadata);

  return {
    markdown: stripped.markdown || content,
    artifacts: dedupeArtifacts([
      ...metadataArtifacts,
      ...stripped.artifacts,
    ]),
    citations: collectCitations(message.metadata),
  };
}

const ChatMessageList: React.FC<Props> = ({ messages, toolTraces = [], loading }) => {
  const endRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, toolTraces.length]);

  return (
    <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
      {loading && !messages.length ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading chat history...
          </Typography>
        </Box>
      ) : null}

      {!loading && !messages.length ? (
        <Box
          sx={{
            height: '100%',
            minHeight: 260,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Box>
            <Typography variant="h6">Start a conversation</Typography>
            <Typography variant="body2" color="text.secondary">
              Ask a question, use DRIFT retrieval, or allow MCP tools to assist.
            </Typography>
          </Box>
        </Box>
      ) : null}

      <Stack spacing={2}>
        {messages.map((message, idx) => {
          const isUser = message.role === 'user';
          const renderPayload = buildMessageRenderPayload(message);

          return (
            <Box
              key={message.id || `${idx}-${message.role}`}
              display="flex"
              justifyContent={isUser ? 'flex-end' : 'flex-start'}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  maxWidth: isUser ? '86%' : '94%',
                  width: isUser ? 'auto' : 'fit-content',
                  minWidth: isUser ? undefined : renderPayload.artifacts.length ? 'min(760px, 94%)' : undefined,
                  bgcolor: isUser ? 'primary.main' : 'background.paper',
                  color: isUser ? 'primary.contrastText' : 'text.primary',
                  borderColor: isUser ? 'primary.main' : 'divider',
                }}
              >
                {isUser ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                ) : (
                  <>
                    {renderPayload.markdown ? (
                      <Box sx={{ '& p': { my: 0.5 } }}>
                        <StbMarkdown>{renderPayload.markdown}</StbMarkdown>
                      </Box>
                    ) : null}

                    <ChatArtifactsRenderer artifacts={renderPayload.artifacts} />
                  </>
                )}

                {renderPayload.citations.length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} useFlexGap>
                    {renderPayload.citations.slice(0, 6).map((citation, cidx) => (
                      <Chip
                        key={cidx}
                        size="small"
                        variant="outlined"
                        label={citation.label}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Paper>
            </Box>
          );
        })}

        {toolTraces.length ? (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
            <Typography variant="caption" fontWeight={700}>
              MCP activity
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} useFlexGap>
              {toolTraces.slice(-10).map((trace, idx) => (
                <Chip
                  key={idx}
                  size="small"
                  color={
                    trace.status === 'error'
                      ? 'error'
                      : trace.status === 'success'
                        ? 'success'
                        : 'default'
                  }
                  variant="outlined"
                  label={`${trace.capability_name || 'tool'}: ${trace.status || 'running'}`}
                />
              ))}
            </Stack>
          </Paper>
        ) : null}

        <div ref={endRef} />
      </Stack>
    </Box>
  );
};

export default ChatMessageList;